import { useEffect } from 'react';
import { CreditCard, Users, Building2, Sparkles, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBillingInfo } from '@/api/hooks/useSettings';
import { RequireRole } from '@/components/auth/RequireRole';
import { cn } from '@/lib/utils';

const TIER_STATIC_FEATURES: Record<string, string[]> = {
  starter: ['TPRM module', 'Basic reporting', 'Email support'],
  business: ['All modules', 'Advanced analytics', 'API access', 'Priority support'],
  enterprise: ['All modules', 'Custom integrations', 'Dedicated CSM', 'SLA guarantee'],
};

function buildFeatures(tier: string, vendorLimit: number | null | undefined, userLimit: number | null | undefined): string[] {
  const vendorStr = vendorLimit != null && vendorLimit > 0 ? `Up to ${vendorLimit.toLocaleString()} vendors` : 'Unlimited vendors';
  const userStr = userLimit != null && userLimit > 0 ? `Up to ${userLimit.toLocaleString()} users` : 'Unlimited users';
  const staticFeatures = TIER_STATIC_FEATURES[tier] ?? TIER_STATIC_FEATURES.starter;
  return [vendorStr, userStr, ...staticFeatures];
}

function SoftCapBar({
  label,
  icon,
  count,
  effectiveLimit,
  overage,
  enforcementMode,
}: {
  label: string;
  icon: React.ReactNode;
  count: number;
  effectiveLimit: number;
  overage: number;
  enforcementMode: string;
}) {
  if (effectiveLimit === -1) {
    return (
      <div className="flex items-center justify-between text-sm">
        <span className="flex items-center gap-1.5 text-text-secondary">{icon} {label}</span>
        <span className="font-mono text-text-primary">{count.toLocaleString()} / ∞</span>
      </div>
    );
  }

  const pct = Math.min((count / effectiveLimit) * 100, 100);
  const isOver = overage > 0;
  const isApproaching = !isOver && pct >= 80;
  const barColor = isOver ? 'bg-accent-rose' : isApproaching ? 'bg-accent-amber' : 'bg-accent-emerald';
  const textColor = isOver ? 'text-accent-rose' : isApproaching ? 'text-accent-amber' : 'text-text-primary';
  const showThresholdMarker = enforcementMode === 'soft_warn' && (isOver || isApproaching);

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="flex items-center gap-1.5 text-text-secondary">{icon} {label}</span>
        <span className={cn('font-mono font-medium', textColor)}>
          {count.toLocaleString()} / {effectiveLimit.toLocaleString()}
          {isOver && <span className="ml-1 text-xs font-normal">(+{overage.toLocaleString()} over)</span>}
        </span>
      </div>
      <div className="relative h-2 w-full rounded-full bg-bg-surface-2 overflow-hidden">
        <div className={cn('h-full rounded-full transition-all', barColor)} style={{ width: `${pct}%` }} />
        {showThresholdMarker && (
          <div className="absolute top-0 bottom-0 w-px bg-accent-amber/60" style={{ left: '80%' }} />
        )}
      </div>
    </div>
  );
}

export function BillingPage() {
  return (
    <RequireRole roles={['super_admin']} label="Billing & Plans">
      <BillingPageBody />
    </RequireRole>
  );
}

function BillingPageBody() {
  useEffect(() => { document.title = 'Billing & Plans — VeriGRC'; }, []);
  const { data: billing, isLoading } = useBillingInfo();

  if (isLoading) return <div className="space-y-4"><Skeleton className="h-48 w-full" /><Skeleton className="h-32 w-full" /></div>;

  const tier = billing?.accountSegment ?? 'starter';
  const features = buildFeatures(tier, billing?.vendor_limit, billing?.user_limit);
  const vendorOverage = billing?.vendor_overage ?? 0;
  const userOverage = billing?.user_overage ?? 0;
  const hasOverage = vendorOverage > 0 || userOverage > 0;

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Billing & Plans</h1>
        <p className="mt-1 text-sm text-text-secondary">Plan and usage overview</p>
      </div>

      {/* Overage alert — shown when any dimension is over its soft cap */}
      {hasOverage && (
        <Card className="border-accent-rose/30 bg-accent-rose/5">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-accent-rose shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-text-primary text-sm">You are over your plan quota</p>
              <p className="text-xs text-text-secondary mt-0.5">
                {[
                  vendorOverage > 0 && `${vendorOverage} vendor${vendorOverage !== 1 ? 's' : ''} over limit`,
                  userOverage > 0 && `${userOverage} user${userOverage !== 1 ? 's' : ''} over limit`,
                ].filter(Boolean).join(' · ')}
                {' '}— new items cannot be added until you upgrade.
              </p>
            </div>
            <Button size="sm" asChild className="shrink-0">
              <a href="mailto:sales@verigrc.com">Upgrade Plan</a>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Current plan */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <CreditCard className="h-4 w-4" /> Current Plan
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Badge variant="sky" className="text-sm px-3 py-1 capitalize font-bold">{tier}</Badge>
            <Badge variant={billing?.status === 'active' ? 'emerald' : 'amber'} className="text-[10px]">
              {billing?.status ?? 'active'}
            </Badge>
          </div>

          {billing?.trial_ends_at && (
            <div className="rounded-lg border border-accent-amber/30 bg-accent-amber/5 px-3 py-2">
              <p className="text-sm text-accent-amber">
                Trial ends: <span className="font-medium">{billing.trial_ends_at.substring(0, 10)}</span>
              </p>
            </div>
          )}

          {billing?.contracted_until && (
            <div className="text-sm text-text-secondary">
              Contract end: <span className="text-text-primary">{billing.contracted_until.substring(0, 10)}</span>
            </div>
          )}

          {/* Usage limits */}
          <div className="space-y-3 pt-2">
            <SoftCapBar
              label="Vendors"
              icon={<Building2 className="h-4 w-4" />}
              count={billing?.vendor_count ?? 0}
              effectiveLimit={billing?.vendor_effective_limit ?? -1}
              overage={vendorOverage}
              enforcementMode={billing?.vendor_enforcement_mode ?? 'monitor'}
            />
            <SoftCapBar
              label="Users"
              icon={<Users className="h-4 w-4" />}
              count={billing?.user_count ?? 0}
              effectiveLimit={billing?.user_effective_limit ?? -1}
              overage={userOverage}
              enforcementMode={billing?.user_enforcement_mode ?? 'monitor'}
            />
          </div>

          {/* Features */}
          <div>
            <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2">Included</p>
            <ul className="space-y-1">
              {features.map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-text-secondary">
                  <span className="h-1.5 w-1.5 rounded-full bg-accent-cyan shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Upgrade CTA — shown for non-enterprise when not already in overage alert */}
      {tier !== 'enterprise' && !hasOverage && (
        <Card className="border-accent-cyan/20 bg-accent-cyan/5">
          <CardContent className="p-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Sparkles className="h-5 w-5 text-accent-cyan shrink-0" />
              <div>
                <p className="font-medium text-text-primary text-sm">Need more capacity?</p>
                <p className="text-xs text-text-secondary">Talk to sales about upgrading your plan</p>
              </div>
            </div>
            <Button size="sm" asChild>
              <a href="mailto:sales@verigrc.com">Contact Sales</a>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
